(ns nosco.ui.ai.chat-parts
  "Presentation-only rendering for canonical Vercel UIMessage parts."
  (:require
   ["ai" :refer [getToolName isToolUIPart]]
   ["react" :as React]
   [clojure.string :as string]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.ai.context-budget :as context-budget]
   [nosco.ai.tool-catalogue :as tool-catalogue]
   [nosco.ai.vercel-chat :as vercel-chat]
   [nosco.ai.vercel-tool-bridge :as tool-bridge]
   [nosco.ai.attachments :as attachments]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.ui.animations :as animations]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.checkbox :as checkbox]
   [nosco.ui.chips :as chips]
   [nosco.ui.dropdowns2 :as dropdowns2]
   [nosco.ui.html :as ui-html]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.menus :as menus]
   [nosco.ui.radio :as radio]
   [nosco.ui.text :as text]
   [nosco.util.markdown :as markdown]))

(defn- nonblank [value]
  (when (and (string? value) (not (string/blank? value))) value))

(defn- payload->clj [value]
  (when-not (nil? value)
    (js->clj value :keywordize-keys true)))

(defn- payload->text [value]
  (cond
    (nil? value) nil
    (string? value) value
    :else
    (try
      (.stringify js/JSON (clj->js (payload->clj value)) nil 2)
      (catch :default _
        (pr-str (payload->clj value))))))

(defn- run-action! [set-submitting! set-error! action!]
  (set-submitting! true)
  (set-error! nil)
  (try
    (-> (js/Promise.resolve (action!))
        (.then (fn [accepted?]
                 (set-submitting! false)
                 (when (false? accepted?)
                   (set-error! (t/t "This interaction is no longer available.")))))
        (.catch (fn [error]
                  (set-submitting! false)
                  (set-error! (t/t "The interaction could not be completed."))
                  (when-not (= "AbortError" (.-name error))
                    (js/console.error "AI Chat interaction failed" error)))))
    (catch :default error
      (set-submitting! false)
      (set-error! (t/t "The interaction could not be completed."))
      (js/console.error "AI Chat interaction failed" error))))

(def ^:private markdown-base-class
  ["nosco-chat-msg__markdown"
   "nosco-redactor-text"
   "nosco-redactor-text--document-compact"])

(defnc CommittedMarkdownBlock
  "A completed streaming block. React.memo keeps its parsed and sanitized DOM
   stable while later blocks continue streaming."
  [{:keys [content]}]
  {:wrap [(React/memo)]}
  (when-let [html (markdown/prepare-streaming-ai-markdown content false)]
    [ui-html/SafeHTMLStrict
     {:html html
      :class "nosco-chat-msg__markdown-block"}]))

(defnc MarkdownBody
  "Assistant Markdown. While streaming, completed blocks are memoized React
   children and only the active tail is re-parsed per chunk. The streaming
   output stays one flex item, like the settled single container, so the
   message's flex gap never opens between committed and active blocks."
  [{:keys [content streaming?]}]
  (let [content (or content "")]
    (if streaming?
      (let [{:keys [blocks active]} (markdown/split-streaming-blocks content)
            active-html (markdown/prepare-streaming-ai-markdown active true)]
        [:div
         (when (seq blocks)
           [:div {:key "committed"
                  :class markdown-base-class}
            (map-indexed (fn [index block]
                           [CommittedMarkdownBlock {:key index :content block}])
                         blocks)])
         (when active-html
           [ui-html/SafeHTMLStrict
            {:key (str "active-" (count blocks))
             :html active-html
             :class (conj markdown-base-class
                          "nosco-chat-msg__markdown--activity-slot"
                          "nosco-chat-msg__markdown--streaming"
                          "nosco-chat-msg__markdown--reveal")}])
         [:span {:key "status" :class "sr-only" :role "status"} (t/t "AI is working")]])
      (when-let [html (markdown/prepare-streaming-ai-markdown content false)]
        [ui-html/SafeHTMLStrict
         {:html html
          :class (conj markdown-base-class "nosco-chat-msg__markdown--activity-slot")}]))))

(defnc ActivityIndicator [{:keys [label]}]
  [:div {:class "nosco-chat-progress" :role "status"
         :aria-label (or label (t/t "AI is working"))}
   [:span {:class "nosco-chat-pending-dot" :aria-hidden true}]
   (when label
     [text/Text {:el :span :variant text/Body-Sm-Regular} label])])

(defnc ToolRunningIndicator []
  [:span {:class "nosco-chat-tool-running-indicator" :aria-hidden true}])

(defnc ToolActivity
  "Compact presentation for a read or write tool phase. `phase` records the real
   lifecycle distinction even where preparing and running intentionally share
   one visual treatment. `tokens` is the estimated size of the result the model
   received, shown in the details."
  [{:keys [phase description completed-description proposed-description details
           tokens]}]
  (let [generic-working? (and (= :preparing phase) (nil? (nonblank description)))
        definition-streaming? (and (= :preparing phase) (not generic-working?))
        running? (= :running phase)
        inspectable? (contains? #{:running :succeeded :failed :rejected :deferred :postponed} phase)
        label (case phase
                :preparing (or (nonblank description) (t/t "Working…"))
                :running (or (nonblank description) (t/t "Working…"))
                :succeeded (or (nonblank completed-description) (t/t "Completed"))
                :failed (t/t "Error: %s" (or (nonblank description)
                                             (t/t "Working…")))
                :rejected (or (nonblank proposed-description) (t/t "Proposed action"))
                :deferred (t/t "Deferred until the conversation was summarised: %s"
                               (or (nonblank description) (t/t "Working…")))
                :postponed (t/t "Postponed to the next step: %s"
                                (or (nonblank description) (t/t "Working…"))))
        row-attrs {:class ["nosco-chat-tool-activity"
                           (when (contains? #{:rejected :deferred :postponed} phase)
                             "nosco-chat-tool-activity--rejected")]
                   :data-tool-phase (name phase)}
        row-children [(when generic-working?
                        [:span {:class "nosco-chat-pending-dot" :aria-hidden true}])
                      [text/Text {:el :span :variant text/Body-Sm-Regular
                                  :class "nosco-chat-tool-activity__label"}
                       label]
                      (when definition-streaming?
                        [:span {:class "nosco-chat-pending-dot" :aria-hidden true}])
                      (when running?
                        [ToolRunningIndicator])]
        row-children (remove nil? row-children)]
    (if inspectable?
      [:details {:class "nosco-chat-tool-activity-disclosure"
                 :data-tool-phase (name phase)}
       (into [:summary row-attrs] row-children)
       [:div {:class "nosco-chat-tool-activity__details"}
        [text/Text {:el :div :variant text/Body-Sm-Regular
                    :class "nosco-chat-tool-activity__full-label"}
         label]
        (when (number? tokens)
          [text/Text {:el :div :variant text/Body-Sm-Regular
                      :class "nosco-chat-tool-activity__tokens"}
           (t/t "≈%s tokens sent to the model" tokens)])
        (when-let [details-text (payload->text details)]
          [text/Text {:el :pre :variant text/Body-Sm-Regular
                      :class "nosco-chat-tool-activity__raw"}
           details-text])]]
      (into [:div row-attrs] row-children))))

(declare ChatActionButton)

(defnc AskUserChoiceSurface
  "Proposed blocking multiple-choice question over one canonical ask_user call.
   Selection is transient form state; the session owns the tool answer."
  [{:keys [question choices max-selections allow-other? preparing? tool-call-id on-answer on-stop]}]
  (let [[selected-ids set-selected-ids!] (hooks/useState #{})
        [other-selected? set-other-selected!] (hooks/useState false)
        [other-text set-other-text!] (hooks/useState "")
        [submitting? set-submitting!] (hooks/useState false)
        [action-error set-action-error!] (hooks/useState nil)
        option-ids (mapv :id choices)
        valid-choices?
        (and (vector? choices)
             (<= 2 (count choices) 8)
             (every? #(and (nonblank (:id %)) (nonblank (:label %))) choices)
             (= (count option-ids) (count (distinct option-ids)))
             (or (nil? max-selections) (pos-int? max-selections)))
        _ (when (and (not preparing?) (not valid-choices?))
            (throw (js/Error. "Invalid ask_user choices reached the question UI.")))
        single-answer? (= 1 max-selections)
        selection-count (+ (count selected-ids) (if other-selected? 1 0))
        at-limit? (and (pos-int? max-selections)
                       (>= selection-count max-selections))
        toggle! (fn [id]
                  (set-selected-ids!
                   (fn [selected]
                     (cond
                       (contains? selected id) (disj selected id)
                       (and (pos-int? max-selections)
                            (>= (+ (count selected)
                                   (if other-selected? 1 0))
                                max-selections)) selected
                       :else (conj selected id)))))
        other-answer (nonblank (string/trim other-text))
        answer {:selected-choice-ids
                (->> choices
                     (filter #(contains? selected-ids (:id %)))
                     (mapv :id))
                :other-text (when other-selected? other-answer)}
        valid-answer? (and (not preparing?) valid-choices?
                           (or (seq selected-ids) other-selected?)
                           (or (not other-selected?) other-answer))
        submit! (fn []
                  (when valid-answer?
                    (run-action! set-submitting! set-action-error!
                                 #(on-answer tool-call-id answer))))]
    [:section {:class "nosco-chat-ask-user-surface"
               :aria-label (t/t "Answer question")}
     [text/Text {:el :div :variant text/Body-Sm-Bold}
      (or (nonblank question) (t/t "Please choose an answer"))]
     (if preparing?
       [ActivityIndicator {:label (t/t "Preparing question")}]
       [:div {:class "nosco-chat-ask-user-surface__choices"}
        (for [{:keys [id label]} choices]
          (if single-answer?
            [radio/Radio
             {:key id
              :id (str "ask-user-" tool-call-id "-" id)
              :name (str "ask-user-" tool-call-id)
              :label label
              :aria-label label
              :wrap-label? true
              :checked (contains? selected-ids id)
              :disabled submitting?
              :container-props {:class "nosco-chat-ask-user-surface__choice"}
              :on-change #(do (set-selected-ids! #{id})
                              (set-other-selected! false))}]
            [checkbox/CheckboxWithLabel
             {:key id
              :id (str "ask-user-" tool-call-id "-" id)
              :variant :small
              :label label
              :aria-label label
              :wrap-label? true
              :checked (contains? selected-ids id)
              :disabled submitting?
              :container-props {:class "nosco-chat-ask-user-surface__choice"}
              :on-change #(toggle! id)}]))
        (when allow-other?
          [:div {:class "nosco-chat-ask-user-surface__other"}
           [(if single-answer? radio/Radio checkbox/CheckboxWithLabel)
            {:id (str "ask-user-" tool-call-id "-other")
             :variant :small
             :name (str "ask-user-" tool-call-id)
             :label (t/t "Other")
             :aria-label (t/t "Other")
             :wrap-label? true
             :checked other-selected?
             :disabled (or submitting? (and (not single-answer?) at-limit? (not other-selected?)))
             :container-props {:class "nosco-chat-ask-user-surface__choice"}
             :on-change #(if single-answer?
                           (do (set-selected-ids! #{}) (set-other-selected! true))
                           (set-other-selected! (not other-selected?)))}]
           (when other-selected?
             [inputs/TextInput
              {:size :small
               :value other-text
               :disabled submitting?
               :focus-on-mount? true
               :aria-label (t/t "Other answer")
               :placeholder (t/t "Please specify…")
               :on-change #(set-other-text! (.. % -target -value))}])])])
     [:div {:class "nosco-chat-ask-user-surface__decisions"}
      [buttons2/Button {:variant buttons2/NewPrimary
                        :size buttons2/SizeSmall
                        :label (if submitting? (t/t "Sending…") (t/t "Send answer"))
                        :disabled (or submitting? (not valid-answer?))
                        :on-click submit!}]
      [ChatActionButton {:action :stop
                         :disabled submitting?
                         :on-click on-stop}]]
     (when action-error
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-card__action-error"}
        action-error])]))

(defn- approval-action [part]
  (let [tool-name (getToolName part)
        input (payload->clj (.-input part))]
    {:id (.-toolCallId part)
     :title (or (nonblank (tool-bridge/proposed-description input))
                (tool-catalogue/tool-human-label tool-name)
                (t/t "Proposed action"))
     :tool-name tool-name
     :arguments (tool-bridge/strip-presentation-input input)}))

(defnc ApprovalActionRow [{:keys [action selected? on-toggle single-action?]}]
  (let [[expanded? set-expanded!] (hooks/useState false)
        {:keys [title tool-name arguments]} action]
    [:div {:class "nosco-chat-approval-surface__action"}
     [:div {:class "nosco-chat-approval-surface__summary"}
      (if single-action?
        [text/Text {:el :span :variant text/Body-Sm-Regular
                    :class "nosco-chat-approval-surface__title"}
         title]
        [checkbox/CheckboxWithLabel {:variant :small
                                     :label title
                                     :checked selected?
                                     :aria-label title
                                     :container-props {:class "nosco-chat-approval-surface__title"}
                                     :on-change on-toggle}])
      [:button {:type "button"
                :class "button-reset nosco-chat-approval-surface__expand"
                :aria-expanded expanded?
                :aria-label (t/t "Show details")
                :on-click #(set-expanded! (not expanded?))}
       [icons2/Icon {:icon icons2/ChevronDown-small
                     :class (when expanded?
                              "nosco-chat-approval-surface__chevron--open")}]]]
     [animations/Collapse2 {:show expanded?}
      [:div {:class "nosco-chat-approval-surface__raw"
             :aria-hidden (not expanded?)}
       [text/Text {:el :div :variant text/Body-Sm-Regular} tool-name]
       [text/Text {:el :pre :variant text/Body-Sm-Regular
                   :class "nosco-chat-approval-surface__arguments"}
        (payload->text arguments)]]]]))

(defnc ChatActionButton
  "Presentational Send/Stop pair for the composer and transient approval UI.
   Remove is the temporary Stop glyph until René supplies the icon set."
  [{:keys [action on-click disabled type]}]
  (let [{:keys [icon tone label]}
        (case action
          :send {:icon icons2/Return-small
                 :tone buttons2/ToneBrand
                 :label (t/t "Send")}
          :stop {:icon icons2/Remove-small
                 :tone buttons2/ToneError
                 :label (t/t "Stop")})]
    [buttons2/Button {:variant buttons2/NewPrimary
                      :size buttons2/SizeSmall
                      :tone tone
                      :icon icon
                      :aria-label label
                      :title label
                      :type (or type "button")
                      :disabled disabled
                      :on-click on-click}]))

(defn- attachment-status-text [{:keys [status truncated? error]}]
  (case status
    :uploading (t/t "Uploading…")
    :reading (t/t "Reading…")
    :extracting (t/t "Reading…")
    :ready (when truncated? (t/t "Truncated"))
    :failed (case error
              "file_too_large" (t/t "Too large, max 50 MB")
              "image_too_large" (t/t "Too large, max 3.75 MB")
              "image_dimensions_too_large" (t/t "Too large, max 8000 pixels per side")
              ("unsupported_type" "unsupported_mime" "not_extractable")
              (t/t "Unsupported file type")
              "extraction_timeout" (t/t "Took too long to read. Retry?")
              (t/t "Could not read. Retry?"))))

(defnc AttachmentPills
  "Pending composer attachments (`nosco.ai.attachments`). Each can be removed
   until the message is sent; a failed one retries on click."
  [{:keys [items on-remove on-retry]}]
  (when (seq items)
    [:div {:class "nosco-chat-panel__attachments"}
     [chips/ChipContainer {:gap :small}
      (for [{:keys [id status] :as item} items]
        [chips/ChipImpl {:key id
                         :variant :boxy
                         :size-class "nosco-chip2--size-small"
                         :avatar (when (:url item)
                                   [:img {:class "nosco-chat-image" :src (:url item) :alt (t/t "Attached image")}])
                         :icon (when-not (:url item)
                                 (if (attachments/image? item) icons2/Image-small icons2/File-small))
                         :color (when (= :failed status) :red)
                         :text (attachment-status-text item)
                         :on-click (when (= :failed status) #(on-retry id))
                         :on-remove #(on-remove id)}])]]))

(defnc SentAttachments
  "The files a sent user message carried (its `attachments` metadata)."
  [{items :attachments}]
  [:div {:class "nosco-chat-msg__attachments"}
   [chips/ChipContainer {:gap :small}
    (for [{:keys [id url] :as item} items]
      [chips/ChipImpl {:key id
                       :variant :boxy
                       :size-class "nosco-chip2--size-small"
                       :avatar (when url [:img {:class "nosco-chat-image" :src url :alt (t/t "Attached image")}])
                       :icon (when-not url
                               (if (attachments/image? item) icons2/Image-small icons2/File-small))}])]])

(defnc ChatComposerTools
  "Bottom toolbar slots. Without `on-attach-files` (the `gamma-media` flag is
   off) there is no attachment menu. Images and documents use the same local
   attachment lifecycle; speech remains unavailable."
  [{:keys [on-attach-files accept]}]
  (let [input-ref (React/useRef nil)
        image-ref (React/useRef nil)]
    [:div {:class "nosco-chat-panel__composer-tools"}
     (when on-attach-files
       [:<>
        [:input {:ref input-ref
                 :type "file"
                 :multiple true
                 :accept accept
                 :hidden true
                 :on-change (fn [event]
                              (let [input (.-target event)]
                                (on-attach-files (array-seq (.-files input)))
                                ;; so choosing the same file again fires a change
                                (set! (.-value input) "")))}]
        [:input {:ref image-ref :type "file" :multiple true
                 :accept attachments/image-accept :hidden true
                 :on-change (fn [event]
                              (let [input (.-target event)]
                                (on-attach-files (array-seq (.-files input)))
                                (set! (.-value input) "")))}]
        [dropdowns2/Dropdown
         {:button (hx/f
                   [buttons2/Button {:variant buttons2/NewTextSubtle
                                     :size buttons2/SizeSmall
                                     :icon icons2/Plus-small
                                     :aria-label (t/t "Add attachment")
                                     :title (t/t "Add attachment")}])
          :popper-options {:placement "top-start" :flip true}}
         (fn [{:keys [close]}]
           [menus/Menu {:keyboard-nav true}
            [menus/MenuSection
             [menus/MenuItems
              [menus/MenuItem {:el :button
                               :label (t/t "File")
                               :aria-label (t/t "File")
                               :icon icons2/File-small
                               :on-click (fn []
                                           (close)
                                           (some-> (.-current input-ref) .click))}]
              [menus/MenuItem {:el :button
                               :label (t/t "Image")
                               :aria-label (t/t "Image")
                               :icon icons2/Image-small
                               :on-click (fn []
                                           (close)
                                           (some-> (.-current image-ref) .click))}]]]])]])
     [buttons2/Button {:variant buttons2/NewTextSubtle
                       :size buttons2/SizeSmall
                       :icon icons2/CallOut-small
                       :aria-label (t/t "Speech input unavailable")
                       :title (t/t "Speech input is not available yet")
                       :disabled true}]]))

(defnc ApprovalSurface
  "One transient current-step decision surface. Selected IDs are ephemeral
   checkbox input; the caller/session remains owner of every SDK response."
  [{:keys [parts preparing? summary-title summary-justification
           on-approve-all on-selected on-reject-all on-stop]}]
  (let [[selected-ids set-selected-ids!] (hooks/useState #{})
        [submitting? set-submitting!] (hooks/useState false)
        [action-error set-action-error!] (hooks/useState nil)
        actions (mapv approval-action parts)
        single-action? (= 1 (count actions))
        decide! (fn [action!]
                  (run-action! set-submitting! set-action-error! action!))]
    [:section {:class "nosco-chat-approval-surface"
               :aria-label (t/t "Review proposed actions")}
     [text/Text {:el :div :variant text/Body-Sm-Bold}
      (or (when-not preparing? (nonblank summary-title))
          (when preparing? (t/t "Review proposed actions"))
          (if single-action?
            (t/t "Review proposed action")
            (t/t "Review %s proposed actions" (count actions))))]
     (when-let [justification (when-not preparing?
                                (nonblank summary-justification))]
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-approval-surface__justification"}
        justification])
     (when-not preparing?
       [:div {:class "nosco-chat-approval-surface__actions"}
        (for [{:keys [id] :as action} actions]
          [ApprovalActionRow
           {:key id
            :action action
            :single-action? single-action?
            :selected? (contains? selected-ids id)
            :on-toggle #(set-selected-ids!
                         (fn [selected]
                           (if (contains? selected id)
                             (disj selected id)
                             (conj selected id))))}])])
     [:div {:class "nosco-chat-approval-surface__decisions"}
      (if preparing?
        [ActivityIndicator {:label (t/t "Preparing actions")}]
        [:<>
         [buttons2/Button {:variant buttons2/NewPrimary
                           :size buttons2/SizeSmall
                           :class "nosco-chat-approval-surface__approve"
                           :label (if (empty? selected-ids)
                                    (if single-action?
                                      (t/t "Approve")
                                      (t/t "Approve all"))
                                    (t/t "Approve selected"))
                           :disabled submitting?
                           :on-click #(decide!
                                       (if (empty? selected-ids)
                                         on-approve-all
                                         (fn [] (on-selected selected-ids))))}]
         [buttons2/Button {:variant buttons2/NewTextSubtle
                           :size buttons2/SizeSmall
                           :label (t/t "Reject")
                           :disabled submitting?
                           :on-click #(decide! on-reject-all)}]])
      [:div {:class "nosco-chat-approval-surface__stop"}
       [ChatActionButton {:action :stop
                          :disabled submitting?
                          :on-click on-stop}]]]
     (when action-error
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-card__action-error"}
        action-error])]))

(defnc ToolPartCard
  [{:keys [part question-actionable?]}]
  (let [tool-name (getToolName part)
        presentation-input (payload->clj (.-input part))
        input (tool-bridge/strip-presentation-input presentation-input)
        output (payload->clj (.-output part))
        ask-user? (= "ask_user" tool-name)
        navigation? (= "suggest_navigation" tool-name)
        resource? (= :resource (tool-catalogue/tool-category tool-name))]
    (cond
      (and ask-user? question-actionable?)
      nil

      (and navigation? (= "output-available" (.-state part))
           (true? (:ok output)))
      (let [{:keys [href label]} (get-in output [:raw :data :navigation])]
        [buttons2/Link {:variant buttons2/NewDefault
                        :size buttons2/SizeSmall
                        :icon-right icons2/ArrowRight-small
                        :class "nosco-chat-navigation"
                        :href href
                        :label label}])

      navigation?
      (when (contains? #{"input-streaming" "input-available"} (.-state part))
        [ActivityIndicator {:label (t/t "Preparing navigation…")}])

      ;; Not run by the context-budget gate; the model calls it again in its
      ;; next step or once the conversation is summarised. Not an error, not a
      ;; user rejection.
      (and (= "output-denied" (.-state part))
           (contains? #{context-budget/deferred-reason context-budget/postponed-reason}
                      (some-> part .-approval .-reason)))
      (let [reason (.. part -approval -reason)]
        [ToolActivity
         {:phase (if (= context-budget/postponed-reason reason) :postponed :deferred)
          :description (tool-bridge/activity-description presentation-input)
          :details {:input input :reason reason}}])

      (or resource? ask-user?)
      (let [phase (case (.-state part)
                    "input-streaming" :preparing
                    "input-available" :running
                    "output-available" (if (false? (:ok output)) :failed :succeeded)
                    "output-error" :failed
                    "output-denied" :failed
                    :preparing)
            error (case (.-state part)
                    "output-error" (.-errorText part)
                    "output-denied" (some-> part .-approval .-reason)
                    (:error output))
            details (case phase
                      :running input
                      ;; A saved result keeps only its file name in the
                      ;; transcript; show the input, then what the model
                      ;; received as plain text so its line breaks survive.
                      :succeeded (if (get-in output [:raw :savedAs])
                                   (str (payload->text {:input input})
                                        "\n\n" (:modelText output))
                                   {:input input
                                    :output (or (:raw output) output)})
                      :failed {:input input :error error}
                      nil)]
        [ToolActivity
         {:phase phase
          :description (tool-bridge/activity-description presentation-input)
          :completed-description (tool-bridge/completed-description presentation-input)
          :details details
          :tokens (:modelTokens output)}])
      :else
      (when-not (or (= "approval-requested" (.-state part))
                    (vercel-chat/write-tool-preparing? part))
        (let [phase (case (.-state part)
                      "input-streaming" :preparing
                      "approval-responded" (if (false? (some-> part .-approval .-approved))
                                             :rejected :preparing)
                      "input-available" :running
                      "output-available" (if (false? (:ok output)) :failed :succeeded)
                      "output-error" :failed
                      "output-denied" :rejected)
              raw {:tool tool-name :arguments input}
              details (case phase
                        :running raw
                        :succeeded (assoc raw :output (or (:raw output) output))
                        :failed (assoc raw :error (or (:error output)
                                                      (.-errorText part)))
                        :rejected raw
                        nil)]
          [ToolActivity
           {:phase phase
            :description (tool-bridge/activity-description presentation-input)
            :completed-description (tool-bridge/completed-description presentation-input)
            :proposed-description (tool-bridge/proposed-description presentation-input)
            :details details
            :tokens (:modelTokens output)}])))))

(defnc AssistantMessage
  [{:keys [message streaming? pending-question-call-ids]}]
  [:div {:class ["nosco-chat-msg" "nosco-chat-msg--assistant"]}
   (for [[index part] (map-indexed vector (array-seq (.-parts message)))]
     (let [part-type (.-type part)]
       (cond
         (= "text" part-type)
         [MarkdownBody {:key (str "text-" index "-" (or (.-id part) ""))
                        :content (.-text part)
                        :streaming? (and streaming? (not= "done" (.-state part)))}]

         (= "reasoning" part-type)
         nil

         (isToolUIPart part)
         [ToolPartCard
          {:key (str "tool-" index "-" (or (.-toolCallId part) ""))
           :part part
           :question-actionable? (contains? pending-question-call-ids
                                            (.-toolCallId part))}]

         :else nil)))])

(defnc CopyButton [{:keys [label value icon-only? class render-as on-close variant]}]
  (let [[state set-state!] (hooks/useState :ready)
        feedback-label (case state
                         :copied (t/t "Copied")
                         :failed (t/t "Copy failed")
                         label)
        copy! (fn []
                (let [write (.writeText js/navigator.clipboard value)]
                  (when on-close (on-close))
                  (-> write
                      (.then (fn []
                               (when-not (= render-as :menu)
                                 (set-state! :copied)
                                 (js/setTimeout #(set-state! :ready) 1500))))
                      (.catch (fn [error]
                                (when-not (= render-as :menu)
                                  (set-state! :failed))
                                (js/console.error "AI Chat clipboard write failed" error))))))]
    (if (= render-as :menu)
      [menus/MenuItem {:el :button
                       :label feedback-label
                       :class class
                       :disabled (string/blank? value)
                       :on-click copy!}]
      [buttons2/Button
       {:variant (or variant buttons2/NewTextSubtle)
        :size buttons2/SizeSmall
        :class class
        :icon (when icon-only? icons2/DocumentCopy-small)
        :label (when-not icon-only? feedback-label)
        :aria-label feedback-label
        :title feedback-label
        :disabled (string/blank? value)
        :on-click copy!}])))

(defnc AssistantActionMetadata
  "Quiet per-message actions over canonical finish metadata. Duration and
   usage are shown only when the session/provider supplied them."
  [{:keys [message]}]
  (let [metadata (payload->clj (.-metadata message))
        usage (:usage metadata)
        duration-ms (:durationMs metadata)
        input-tokens (:inputTokens usage)
        output-tokens (:outputTokens usage)
        cache-read (get-in usage [:inputTokenDetails :cacheReadTokens])
        parts (cond-> []
                (number? duration-ms)
                (conj (t/t "%ss" (quot (+ duration-ms 500) 1000)))
                (number? input-tokens)
                (conj (t/t "%s in" input-tokens))
                (and (number? input-tokens) (pos? input-tokens)
                     (number? cache-read) (pos? cache-read))
                (conj (t/t "%s cached"
                           (str (min 99 (js/Math.floor (* 100 (/ cache-read input-tokens)))) "%")))
                (number? output-tokens)
                (conj (t/t "%s out" output-tokens)))
        prose (vercel-chat/raw-message-text message)]
    (when (or (not (string/blank? prose)) (seq parts))
      [:div {:class "nosco-chat-msg__action-metadata"}
       (when-not (string/blank? prose)
         [CopyButton {:label (t/t "Copy message")
                      :value prose
                      :icon-only? true
                      :class "nosco-chat-msg__assistant-copy"}])
       (when (seq parts)
         [text/Text {:el :span :variant text/Body-Sm-Regular
                     :class "nosco-chat-msg__action-metadata-text"}
          (string/join " · " parts)])])))
